import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Send } from 'lucide-react';
import { ContactForm as ContactFormType } from '../types';
import emailjs from '@emailjs/browser';
import ScrollToTop from '../components/ScrollToTop';
import WhatsAppButton from '../components/WhatsAppButton';

const Contact: React.FC = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState<ContactFormType>({
    name: '',
    surname: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      // EmailJS ile e-posta gönderimi
      const templateParams = {
        from_name: `${form.name} ${form.surname}`,
        from_email: form.email,
        to_email: 'info@aymersafety.com',
        message: form.message,
        reply_to: form.email
      };

      // EmailJS servis bilgileri (bu bilgileri EmailJS dashboard'unuzdan alacaksınız)
      await emailjs.send(
        'YOUR_SERVICE_ID', // EmailJS Service ID
        'YOUR_TEMPLATE_ID', // EmailJS Template ID
        templateParams,
        'YOUR_PUBLIC_KEY' // EmailJS Public Key
      );

      setSubmitStatus('success');
      setForm({ name: '', surname: '', email: '', message: '' });
    } catch (error) {
      console.error('E-posta gönderimi başarısız:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white py-16">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-amber-400">
            {t('contactTitle')}
          </h1>
          <div className="w-24 h-1 bg-amber-400 mx-auto"></div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Location Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="h-full"
          >
            <h2 className="text-2xl font-bold mb-8 text-amber-400">
              {t('location')}
            </h2>
            <div className="bg-slate-800 rounded-lg shadow-xl p-8 border border-slate-700">
              <div className="text-center mb-8">
                <div className="w-24 h-24 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-12 w-12 text-slate-900" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{t('officeLocation')}</h3>
                <p className="text-slate-300">{t('officeLocationDesc')}</p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3 p-4 bg-slate-700 rounded-lg">
                  <MapPin className="h-5 w-5 text-amber-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">{t('address')}</h4>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      {t('companyAddress')}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 p-4 bg-slate-700 rounded-lg">
                  <Phone className="h-5 w-5 text-amber-400" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">{t('phone')}</h4>
                    <p className="text-slate-300">{t('companyPhone')}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 p-4 bg-slate-700 rounded-lg">
                  <Mail className="h-5 w-5 text-amber-400" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">{t('email')}</h4>
                    <p className="text-slate-300">{t('companyEmail')}</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-slate-600">
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(t('companyAddress'))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-amber-400 hover:bg-amber-500 text-slate-900 font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <MapPin className="h-5 w-5" />
                  <span>{t('openInMaps')}</span>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="h-full"
          >
            <h2 className="text-2xl font-bold mb-8 text-amber-400">
              {t('contactForm')}
            </h2>
            
            <div className="bg-slate-800 rounded-lg shadow-xl p-8 border border-slate-700">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      {t('name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:border-amber-400 transition-colors duration-200 text-white"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="surname" className="block text-sm font-medium mb-2">
                      {t('surname')}
                    </label>
                    <input
                      type="text"
                      id="surname"
                      name="surname"
                      value={form.surname}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:border-amber-400 transition-colors duration-200 text-white"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2">
                    {t('yourEmail')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={form.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:border-amber-400 transition-colors duration-200 text-white"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-2">
                    {t('message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:border-amber-400 transition-colors duration-200 resize-none text-white"
                  ></textarea>
                </div>
                
                {/* Success/Error Messages */}
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-green-800 border border-green-500 rounded-lg text-green-100"
                  >
                    ✅ Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.
                  </motion.div>
                )}
                
                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-800 border border-red-500 rounded-lg text-red-100"
                  >
                    ❌ Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyin veya doğrudan e-posta gönderin.
                  </motion.div>
                )}
                
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-amber-400 hover:bg-amber-500 text-slate-900 font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <Send className="h-5 w-5" />
                      <span>{t('send')}</span>
                    </>
                  )}
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
      
      <ScrollToTop />
      <WhatsAppButton />
    </div>
  );
};

export default Contact;